import React, { useEffect, useState } from "react"
import {
  createADir,
  fetchETLDirectory,
  removeADir,
  removeAFile,
} from "@/api/dags.api"
import useapi from "@/hooks/useapi"
import { Breadcrumb, Button, message, Popconfirm } from "antd"
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  FolderOutlined,
  HomeOutlined,
} from "@ant-design/icons"
import Loading from "@components/spinners/loading"
import { downloadFile } from "@/api/file.api"
import GetInputModal from "@components/getInputModal"
import { CopyToClipboard } from "react-copy-to-clipboard"

interface IFile {
  name: string
}

interface IDirectory {
  name: string
}

type IPaths = {
  dirs: IFile[]
  files: IDirectory[]
}

const ListDirectory = ({
  setCurrentPath,
  currentPath,
}: {
  currentPath: string
  setCurrentPath: any
}) => {
  const {
    data,
    request,
    loading,
  }: { data: IPaths; request: any; loading: boolean } =
    useapi(fetchETLDirectory)

  const { request: requestRmFile, loading: loadingRmFile } = useapi(removeAFile)
  const { request: requestRmDir, loading: loadingRmDir } = useapi(removeADir)
  const { request: requestCreateDir, loading: loadingCreateDir } =
    useapi(createADir)

  const [previousPath, setPreviousPath] = useState<string>("")

  useEffect(() => {
    request()
  }, [])

  if (!data) return null

  /**
   * Component to extract pwd.
   * Extraction preference is on dirs followed by files.
   * @param data: IPaths
   * @param loading: boolean
   */
  const ListPWD = ({ data, loading }: { data: IPaths; loading: boolean }) => {
    if (!data) return <></>

    let parentDirFullPathSplit: string[] = []

    if (data?.dirs.length >= 1) {
      let directories = data?.dirs
      let parentDirFullPath = directories[0].name
      parentDirFullPathSplit = parentDirFullPath.split("/")
    } else if (data?.files.length >= 1) {
      parentDirFullPathSplit = data?.files[0]?.name.split("/")
    } else {
      parentDirFullPathSplit = currentPath.split("/")
    }

    if (parentDirFullPathSplit?.length >= 1) {
      // @ts-ignore
      return (
        <>
          <Breadcrumb
            style={{
              backgroundColor: "black",
              color: "white",
              borderRadius: 5,
              padding: 5,
              cursor: "pointer",
              height: "40px",
              lineHeight: "30px",
            }}
          >
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                justifyContent: "space-between",
                marginLeft: 5,
              }}
            >
              <div>
                <Breadcrumb.Item
                  onClick={() => {
                    request()
                    setCurrentPath("")
                  }}
                  separator={<span style={{ color: "white" }}>/</span>}
                >
                  <HomeOutlined /> Home
                </Breadcrumb.Item>
                {parentDirFullPathSplit.slice(0, -1).map((item, index) => (
                  <Breadcrumb.Item
                    separator={<span style={{ color: "white" }}>/</span>}
                    key={index}
                    onClick={() => {
                      let pathToHere = parentDirFullPathSplit.slice(
                        0,
                        index + 1,
                      )
                      request(pathToHere.join("/"))
                      setCurrentPath(pathToHere.join("/"))
                      setCurrentPath(pathToHere.join("/"))
                    }}
                  >
                    <span style={{ color: "white" }}>{item}</span>
                  </Breadcrumb.Item>
                ))}
              </div>

              <GetInputModal
                currentPath={currentPath}
                onOk={async (value: { value: string }) => {
                  if (currentPath === "" || !currentPath) {
                    await requestCreateDir(value)
                  } else {
                    await requestCreateDir(currentPath + "/" + value)
                  }
                  await request(currentPath)
                }}
              />
            </div>
          </Breadcrumb>
          <Loading loading={loading} />
          {data?.dirs.length == 0 && data?.files.length === 0 && (
            <p style={{ margin: 20 }}>Empty Directory</p>
          )}
        </>
      )
    }
    return null
  }

  const ListDirectories = ({
    directories = [],
  }: {
    directories: IDirectory[]
  }) => {
    if (directories.length === 0) return null

    return (
      <div style={{ marginTop: 10 }}>
        {directories.map((dir, index) => {
          let currentDirectoryName = dir.name.split("/").at(-1)
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #EDF3EF",
              }}
            >
              <div
                style={{
                  margin: 5,
                  fontSize: 16,
                  height: 30,
                  cursor: "pointer",
                }}
                onClick={() => {
                  request(dir.name)
                  setCurrentPath(dir.name)
                  setPreviousPath(dir.name)
                }}
                key={index}
              >
                <FolderOutlined style={{ color: "orange", marginRight: 5 }} />
                <span>{currentDirectoryName}</span>
              </div>

              <div>
                <CopyToClipboard
                  text={dir.name || "/"}
                  onCopy={() => message.success("Path copied to clipboard")}
                >
                  <Button
                    size={"small"}
                    style={{ marginLeft: 10, marginTop: 7 }}
                  >
                    <CopyOutlined />
                  </Button>
                </CopyToClipboard>
                <Popconfirm
                  title="Are you sure to delete?"
                  onConfirm={async () => {
                    await requestRmDir(dir.name)
                    await request(currentPath)
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    danger
                    size={"small"}
                    style={{ float: "right", marginLeft: 10, marginTop: 7 }}
                  >
                    <DeleteOutlined />
                  </Button>
                </Popconfirm>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const ListFiles = ({ files = [] }: { files: IFile[] }) => {
    if (files.length === 0) return null
    return (
      <>
        {files.map((file, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              flex: 1,
              borderBottom: "1px solid #EDF3EF",
            }}
          >
            <div
              style={{
                marginLeft: 5,
                fontSize: 14,
                marginTop: 10,
                flex: 1,
              }}
            >
              <FileOutlined style={{ color: "red", marginRight: 7 }} />
              {file.name.split("/").at(-1)}
            </div>

            <div
              style={{
                flex: 1,
                marginTop: 7,
              }}
            >
              <Popconfirm
                title="Are you sure to delete?"
                onConfirm={async () => {
                  await requestRmFile(file.name)
                  await request(currentPath)
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  danger
                  size={"small"}
                  style={{ float: "right", marginLeft: 10 }}
                >
                  <DeleteOutlined />
                </Button>
              </Popconfirm>

              <CopyToClipboard
                text={file.name || "/"}
                onCopy={() => message.success("Path copied to clipboard")}
              >
                <Button
                  size={"small"}
                  style={{ marginLeft: 10, float: "right" }}
                >
                  <CopyOutlined />
                </Button>
              </CopyToClipboard>

              <Button
                size={"small"}
                style={{ float: "right" }}
                onClick={async () => await downloadFile(file.name)}
              >
                <DownloadOutlined /> Download
              </Button>
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <div>
      <ListPWD
        data={data}
        loading={loading || loadingRmFile || loadingRmDir || loadingCreateDir}
      />
      {data?.dirs.length >= 1 && <ListDirectories directories={data.dirs} />}
      {data?.files.length >= 1 && <ListFiles files={data.files} />}
    </div>
  )
}

export default ListDirectory
